import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './utils/ThemeProvider';
import { ConfirmProvider } from './components/ConfirmDialog';
import { Layout } from './components/Layout';
import { SiteLayout } from './components/site/SiteShell';
import ErrorBoundary from './components/ErrorBoundary';
import { ServerError } from './pages/errors/ServerError';
import { AdminLogin, MemberLogin } from './pages/Login';
import { roleHomePath } from './lib/config';
import { ADMIN_PORTAL_ROLES } from './lib/access';
import {
  saveIntendedRedirect,
  lockPostAuthDestination,
  resetPostAuthDestinationLock,
  sanitizeIntendedPath,
} from './lib/intendedRedirect';

function loginPathForRedirect(redirect) {
  const safe = sanitizeIntendedPath(redirect) || '';
  const path = safe.split('?')[0] || '';
  if (path === '/admin' || path.startsWith('/admin/')) {
    return `/portal?redirect=${encodeURIComponent(safe || '/admin')}`;
  }
  return `/login?redirect=${encodeURIComponent(safe || '/live')}`;
}

const lazyLoad = (importFn, exportName = null) =>
  lazy(() =>
    importFn().then((module) => {
      const component = exportName ? module[exportName] : module.default || module;
      if (!component) {
        throw new Error(`Component ${exportName || 'default'} not found`);
      }
      return { default: component };
    })
  );

const Landing = lazyLoad(() => import('./pages/site'), 'Landing');
const AboutPage = lazyLoad(() => import('./pages/site'), 'AboutPage');
const VisionPage = lazyLoad(() => import('./pages/site'), 'VisionPage');
const WatchPage = lazyLoad(() => import('./pages/site'), 'WatchPage');
const LivePage = lazyLoad(() => import('./pages/site'), 'LivePage');
const EventsPage = lazyLoad(() => import('./pages/site'), 'EventsPage');
const MinistriesPage = lazyLoad(() => import('./pages/site'), 'MinistriesPage');
const MinistryDetailPage = lazyLoad(() => import('./pages/site'), 'MinistryDetailPage');
const TestimoniesPage = lazyLoad(() => import('./pages/site'), 'TestimoniesPage');
const ContactPage = lazyLoad(() => import('./pages/site'), 'ContactPage');
const NewsPage = lazyLoad(() => import('./pages/site'), 'NewsPage');
const NewsDetailPage = lazyLoad(() => import('./pages/site'), 'NewsDetailPage');
const MessagesPage = lazyLoad(() => import('./pages/site'), 'MessagesPage');
const MessageWatchPage = lazyLoad(() => import('./pages/site'), 'MessageWatchPage');

const AdminDashboard = lazyLoad(() => import('./pages/Dashboard'), 'AdminDashboard');
const FtmDashboard = lazyLoad(() => import('./pages/Dashboard'), 'FtmDashboard');
const ChurchDashboard = lazyLoad(() => import('./pages/Dashboard'), 'ChurchDashboard');
const SetMemberDashboard = lazyLoad(() => import('./pages/Dashboard'), 'SetMemberDashboard');
const SetLeaderDashboard = lazyLoad(() => import('./pages/Dashboard'), 'SetLeaderDashboard');
const Profile = lazyLoad(() => import('./pages/Profile'), 'Profile');
const ChurchesPage = lazyLoad(() => import('./pages/Resources'), 'ChurchesPage');
const FtmsPage = lazyLoad(() => import('./pages/Resources'), 'FtmsPage');
const UsersPage = lazyLoad(() => import('./pages/Resources'), 'UsersPage');
const UserDetailPage = lazyLoad(() => import('./pages/UserDetail'), 'UserDetailPage');
const UsersByRolePage = lazyLoad(() => import('./pages/Resources'), 'UsersByRolePage');
const ReportsPage = lazyLoad(() => import('./pages/Resources'), 'ReportsPage');
const ExpensesPage = lazyLoad(() => import('./pages/Resources'), 'ExpensesPage');
const FollowupsPage = lazyLoad(() => import('./pages/FtmWorkflow'), 'FollowupsPage');
const MonitoringPage = lazyLoad(() => import('./pages/FtmWorkflow'), 'MonitoringPage');
const SettingsPage = lazyLoad(() => import('./pages/Settings'), 'SettingsPage');
const RolesPermissionsPage = lazyLoad(() => import('./pages/RolesPermissions'), 'RolesPermissionsPage');
const AnalyticsPage = lazyLoad(() => import('./pages/Analytics'), 'AnalyticsPage');
const LiveProgramsPage = lazyLoad(() => import('./pages/LivePrograms'), 'LiveProgramsPage');
const StreamLinksPage = lazyLoad(() => import('./pages/StreamLinks'), 'StreamLinksPage');
const MemberUpdatesAdminPage = lazyLoad(() => import('./pages/MemberUpdates'), 'MemberUpdatesAdminPage');
const MessagesAdminPage = lazyLoad(() => import('./pages/MessagesAdmin'), 'MessagesAdminPage');
const EcardsAdminPage = lazyLoad(() => import('./pages/EcardsAdmin'), 'EcardsAdminPage');
const ContactDetailsPage = lazyLoad(() => import('./pages/ContactDetails'), 'ContactDetailsPage');
const MemberUpdatesPage = lazyLoad(() => import('./pages/MemberUpdatesFeed'), 'MemberUpdatesPage');
const MemberUpdateDetailPage = lazyLoad(() => import('./pages/MemberUpdatesFeed'), 'MemberUpdateDetailPage');
const NotFound = lazyLoad(() => import('./pages/errors/NotFound'), 'NotFound');
const Forbidden = lazyLoad(() => import('./pages/errors/Forbidden'), 'Forbidden');
const Offline = lazyLoad(() => import('./pages/errors/Offline'), 'Offline');

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
    </div>
  );
}

function PrivateRoute({ children, roles, permissions }) {
  const { isAuthenticated, loading, canAccess } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (isAuthenticated) {
      resetPostAuthDestinationLock();
    }
  }, [isAuthenticated, location.pathname]);

  if (loading) return <PageLoader />;
  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search || ""}`;
    saveIntendedRedirect(redirect);
    return <Navigate to={loginPathForRedirect(redirect)} replace />;
  }

  if ((roles?.length || permissions?.length) && !canAccess({ roles, permissions })) {
    return <Navigate to="/403" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading, homePath, role } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;

  // Stay on login while KingsChat token is being exchanged — Login.goHome handles destination.
  const exchangingToken = new URLSearchParams(location.search).has("token");
  if (isAuthenticated && !exchangingToken) {
    const destination = lockPostAuthDestination(homePath || roleHomePath(role) || "/");
    return <Navigate to={destination} replace />;
  }

  return children;
}

function MembersPage() {
  return <UsersByRolePage role="setmember" title="Set Members" />;
}

function LeadersPage() {
  return <UsersByRolePage role="setleader" title="Set Leaders" />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public website — no sign-in required (except Live / Watch) */}
        <Route element={<SiteLayout />}>
          <Route index element={<Landing />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="vision" element={<VisionPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="ministries" element={<MinistriesPage />} />
          <Route path="ministries/:slug" element={<MinistryDetailPage />} />
          <Route path="testimonies" element={<TestimoniesPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="news" element={<NewsPage />} />
          <Route path="news/:slug" element={<NewsDetailPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:slug" element={<MessageWatchPage />} />
          <Route
            path="live"
            element={
              <PrivateRoute>
                <LivePage />
              </PrivateRoute>
            }
          />
          <Route
            path="watch"
            element={
              <PrivateRoute>
                <WatchPage />
              </PrivateRoute>
            }
          />
        </Route>

        <Route
          path="/login"
          element={
            <PublicRoute>
              <MemberLogin />
            </PublicRoute>
          }
        />
        <Route
          path="/portal"
          element={
            <PublicRoute>
              <AdminLogin />
            </PublicRoute>
          }
        />
        <Route path="/admin/login" element={<Navigate to="/portal" replace />} />

        {/* Authenticated portal */}
        <Route
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route path="profile" element={<Profile />} />

          <Route
            path="admin"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/analytics"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES}>
                <AnalyticsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/churches"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-churches']}>
                <ChurchesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/ftms"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-first-contacts']}>
                <FtmsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-users']}>
                <UsersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/users/:id"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-users']}>
                <UserDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/roles"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-roles']}>
                <RolesPermissionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/reports"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-reports']}>
                <ReportsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/expenses"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-expenses']}>
                <ExpensesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/live-programs"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-live-programs']}>
                <LiveProgramsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/stream-links"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-live-programs']}>
                <StreamLinksPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/member-updates"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-posts']}>
                <MemberUpdatesAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/messages"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-messages']}>
                <MessagesAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/ecards"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-ecards']}>
                <EcardsAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/contact"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-settings']}>
                <ContactDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/settings"
            element={
              <PrivateRoute roles={ADMIN_PORTAL_ROLES} permissions={['view-settings']}>
                <SettingsPage />
              </PrivateRoute>
            }
          />

          <Route path="updates" element={<MemberUpdatesPage />} />
          <Route path="updates/:slug" element={<MemberUpdateDetailPage />} />

          <Route
            path="ftm"
            element={
              <PrivateRoute roles={['ftm']}>
                <FtmDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="ftm/ftms"
            element={
              <PrivateRoute roles={['ftm']} permissions={['view-first-contacts']}>
                <FtmsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ftm/members"
            element={
              <PrivateRoute roles={['ftm']} permissions={['view-ftm-setmembers']}>
                <MembersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ftm/leaders"
            element={
              <PrivateRoute roles={['ftm']} permissions={['view-ftm-setleaders']}>
                <LeadersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ftm/followups"
            element={
              <PrivateRoute roles={['ftm']} permissions={['view-followups']}>
                <FollowupsPage title="Follow-ups" />
              </PrivateRoute>
            }
          />

          <Route
            path="church"
            element={
              <PrivateRoute roles={['church']}>
                <ChurchDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="church/reports"
            element={
              <PrivateRoute roles={['church']} permissions={['view-reports']}>
                <ReportsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="church/expenses"
            element={
              <PrivateRoute roles={['church']} permissions={['view-expenses']}>
                <ExpensesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="church/ftms"
            element={
              <PrivateRoute roles={['church']} permissions={['view-first-contacts']}>
                <FtmsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="set-member"
            element={
              <PrivateRoute roles={['setmember']}>
                <SetMemberDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="set-member/ftms"
            element={
              <PrivateRoute roles={['setmember']} permissions={['view-first-contacts']}>
                <FtmsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="set-member/followups"
            element={
              <PrivateRoute roles={['setmember']} permissions={['view-followups']}>
                <FollowupsPage title="My Follow-ups" mine />
              </PrivateRoute>
            }
          />

          <Route
            path="set-leader"
            element={
              <PrivateRoute roles={['setleader']}>
                <SetLeaderDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="set-leader/ftms"
            element={
              <PrivateRoute roles={['setleader']} permissions={['view-first-contacts']}>
                <FtmsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="set-leader/monitoring"
            element={
              <PrivateRoute roles={['setleader']} permissions={['view-monitorings']}>
                <MonitoringPage title="Monitoring" mine />
              </PrivateRoute>
            }
          />
        </Route>

        <Route path="/403" element={<Forbidden />} />
        <Route path="/500" element={<ServerError />} />
        <Route path="/offline" element={<Offline />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ErrorBoundary>
          <AuthProvider>
            <ConfirmProvider>
              <AppRoutes />
            </ConfirmProvider>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
